import {ICommentModel} from '@src/@interfaces/models/Comment/ICommentModel';
import {arrayUnsetValue} from '@src/helpers/array/arrayUnset';
import {IListener, ISubscription} from '@src/@interfaces/services/Events';
import app from '@src/helpers/app';
import {ICommentsRequest} from '@src/@interfaces/services/CommentRepository/ICommentsRequest';
import EventEmitter from '@src/services/Events/EventEmitter';
import arrayToMap from '@src/helpers/array/arrayToMap';
import {TPromiseCallbacks} from '@src/@types/promise';

type TPreparedResponsePayload = Record<ICommentModel['id'], ICommentModel>;

export default class CommentsRequest extends EventEmitter implements ICommentsRequest {
    #EVENT_COMPLETED = 'completed';

    protected idsToFetch: string[] = [];
    protected state: 'pending' | 'fetching' | 'completed' = 'pending';
    protected timeout?: ReturnType<typeof setTimeout>;
    protected request: {
        controls: Partial<TPromiseCallbacks<TPreparedResponsePayload>>,
        promise: Promise<TPreparedResponsePayload> | null,
    } | null = null;

    constructor(
        protected delayToCollectIdsInMs: number = 1000
    ) {
        super();
    }

    isCompleted(): boolean {
        return this.state === 'completed';
    }

    isFetching(): boolean {
        return this.state === 'fetching';
    }

    isAvailable(): boolean {
        return !this.isCompleted() && !this.isFetching();
    }

    push(commentId: string): Promise<TPreparedResponsePayload> {
        return this.performActionSafelyAndProcessQueue(() => {
            this.idsToFetch.push(commentId);
        });
    }

    forget(commentId: string): void {
        return void this.performActionSafelyAndProcessQueue(() => {
            arrayUnsetValue(this.idsToFetch, commentId);
        });
    }

    onComplete(listener: IListener<TPreparedResponsePayload>): ISubscription {
        return this.once(this.#EVENT_COMPLETED, listener);
    }

    protected fireCompletedEvent(comments: TPreparedResponsePayload): void {
        this.emit(this.#EVENT_COMPLETED, comments);
    }

    protected performActionSafelyAndProcessQueue(callback: () => void) {
        if (this.isAvailable()) {
            callback();
        }

        return this.processQueue();
    }

    protected processQueue(): Promise<TPreparedResponsePayload> {
        clearTimeout(this.timeout);

        if (this.idsToFetch.length && this.state === 'pending') {
            this.timeout = setTimeout(() => this.fetch(), this.delayToCollectIdsInMs);
        }

        if (this.request?.promise) {
            return this.request?.promise;
        }

        const link: typeof this.request = this.request = {
            controls: {},
            promise: null,
        };

        return link.promise = new Promise((resolve, reject) => {
            link.controls = {resolve, reject};
        });
    }

    protected fetch(): Promise<TPreparedResponsePayload> {
        this.state = 'fetching';

        return app('http')
            .route<ICommentModel[]>('comments.get-exact-comments', {}, {
                json: {
                    'controller-name': 'Comments',
                    ids: this.idsToFetch,
                }
            })
            .then(comments => {
                const map = arrayToMap(comments, 'id');
                this.request?.controls?.resolve?.(map);
                this.fireCompletedEvent(map);

                return map;
            })
            .catch(err => {
                this.request?.controls?.reject?.(err);

                throw err;
            })
            .finally(() => {
                this.state = 'completed';
                this.request = null;
            });
    }
}
